import { FC, useState } from 'react';
import {
  cn,
  Tag,
  Badge,
  FaIcon,
  ResourceTexts,
  TResourceGroupType,
} from '@fe-learner-common';
import { faFile } from '@fortawesome/pro-regular-svg-icons/faFile';
import { faPlay } from '@fortawesome/pro-regular-svg-icons/faPlay';
import { faLink } from '@fortawesome/pro-regular-svg-icons/faLink';
import { faVolume } from '@fortawesome/pro-regular-svg-icons/faVolume';
import { IconDefinition } from '@fortawesome/fontawesome-common-types';

type ResourceProps = {
  className?: string;
  resourceId: string;
  title?: string | null;
  groupType: TResourceGroupType;
  thumbnail?: string | null;
  tags: string[];
  isNew?: boolean;
  texts: ResourceTexts;
};

const thumbnailIconMap: Record<
  TResourceGroupType,
  { icon: IconDefinition; className: string }
> = {
  video: {
    icon: faPlay,
    className: 'text-neutral-0',
  },
  image: {
    icon: faFile,
    className: 'text-neutral-0',
  },
  audio: {
    icon: faVolume,
    className: 'text-neutral-500',
  },
  link: {
    icon: faLink,
    className: 'text-neutral-500',
  },
  document: {
    icon: faFile,
    className: 'text-neutral-500',
  },
  other: {
    icon: faFile,
    className: 'text-neutral-500',
  },
};

type ThumbnailProps = {
  type: TResourceGroupType;
  thumbnail?: string | null;
};
const Thumbnail: FC<ThumbnailProps> = ({ type, thumbnail }) => {
  const [isError, setIsError] = useState(false);
  const iconDetails = thumbnailIconMap[type] || thumbnailIconMap.other;
  return (
    <div className="relative aspect-video h-[40px] rounded-lg bg-neutral-100 group-hover:bg-neutral-50">
      {thumbnail && !isError && (
        <img
          src={thumbnail}
          alt=""
          className="h-full w-full rounded-lg object-cover"
          onError={() => setIsError(true)}
        />
      )}
      {isError && (
        <FaIcon
          icon={iconDetails.icon}
          width={16}
          height={16}
          className={cn(
            'absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transform',
            iconDetails.className,
          )}
        />
      )}
    </div>
  );
};

export const Resource: FC<ResourceProps> = ({
  className,
  resourceId,
  title,
  groupType,
  thumbnail,
  tags,
  isNew,
  texts,
}) => {
  return (
    <div className={cn('flex gap-x-4', className)}>
      <Thumbnail type={groupType} thumbnail={thumbnail} />
      <div className="space-y-1">
        <h4 className="break-all text-start text-label-lg text-neutral-900">
          {title}
        </h4>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-body-md text-neutral-500">
            {texts?.groupTypes?.[groupType]}
          </span>
          {tags?.length ? (
            <>
              <span className="text-body-md text-neutral-500">·</span>
              {tags.map((tag) => (
                <Tag
                  key={`resource-tag-${resourceId}-${tag}`}
                  className="group-hover:bg-neutral-50"
                >
                  <span>{tag}</span>
                </Tag>
              ))}
            </>
          ) : null}

          {isNew && (
            <div className="flex items-center gap-x-1">
              <Badge size="sm" type="negative" />
              <span className="text-label-sm text-negative-500">
                {texts?.new}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
